import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function StatusBadge({ status }) {
  const config = {
    paid: { cls: 'badge-success', label: 'PAID' },
    partial: { cls: 'badge-warning', label: 'PARTIAL' },
    pending: { cls: 'badge-info', label: 'PENDING' },
    overdue: { cls: 'badge-danger', label: 'OVERDUE' },
    waived: { cls: 'badge-navy', label: 'WAIVED' },
  };
  const c = config[status] || { cls: 'badge-info', label: status?.toUpperCase() };
  return <span className={`badge ${c.cls}`}>{c.label}</span>;
}

// ─── Phase 2: shared "My Recent Payments" component ────────────────────────
// Lists the member's submissions (or submissions where they are beneficiary).
// Each row collapses by default; tapping expands to show the full allocation
// breakdown — which fee paid, which fine, advance credit consumed, etc.
function PaymentsBreakdownSection() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [breakdownById, setBreakdownById] = useState({}); // submissionId -> result of /allocations
  const [editingId, setEditingId] = useState(null);       // submission being edited
  const [editUtr, setEditUtr] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    api.get('/payments/my').then(r => {
      // /payments/my returns { payments: [...] } already filtered to the user
      setSubmissions(r.data.payments || r.data || []);
    }).catch(() => setSubmissions([])).finally(() => setLoading(false));
  }, []);

  function toggle(id) {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!breakdownById[id]) {
      api.get('/payments/my/' + id + '/allocations')
        .then(r => setBreakdownById(prev => ({ ...prev, [id]: r.data })))
        .catch(() => setBreakdownById(prev => ({ ...prev, [id]: { error: true } })));
    }
  }

  // Base (real bank reference) of a UTR — everything before "_SPLIT_".
  function baseUtr(u) { return String(u || '').split('_SPLIT_')[0]; }

  // A member may correct the UTR while the payment is still pending and has not
  // been matched/confirmed. Split payments are editable too: the edit re-bases
  // every part of the split group in one go (handled server-side).
  function isEditable(s) {
    return s.status === 'pending'
      && s.reconcile_status !== 'matched'
      && s.reconcile_status !== 'confirmed';
  }

  function startEdit(s) {
    setEditingId(s.id);
    setEditUtr(baseUtr(s.utr));   // edit the real bank reference, not the _SPLIT_ child id
    setEditError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditUtr('');
    setEditError('');
  }

  function saveUtr(id) {
    const v = editUtr.trim().toUpperCase();
    if (!v) { setEditError('Transaction reference is required.'); return; }
    setEditSaving(true);
    setEditError('');
    api.patch('/payments/my/submission/' + id, { utr: v })
      .then(r => {
        // The server re-bases the whole split group and returns every affected
        // row, so refresh them all — not just the one that was clicked.
        const group = (r.data && r.data.group) ? r.data.group
                    : (r.data && r.data.submission) ? [r.data.submission] : [];
        if (group.length) {
          const byId = new Map(group.map(g => [String(g.id), g]));
          setSubmissions(prev => prev.map(s => byId.has(String(s.id))
            ? { ...s, utr: byId.get(String(s.id)).utr }
            : s));
        }
        cancelEdit();
      })
      .catch(err => {
        setEditError(err?.response?.data?.error || 'Could not update. Please try again.');
      })
      .finally(() => setEditSaving(false));
  }

  if (loading) return null;
  if (submissions.length === 0) return null;

  // Show last 8 — anything older is in the noise zone for a member
  const visible = submissions.slice(0, 8);

  return (
    <div style={{ background:'white', borderRadius:12, border:'1px solid #e5e7eb',
                  padding:'14px 16px', marginTop:'var(--space-md)' }}>
      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1rem',
                    color:'#1E3A6E', marginBottom:10, letterSpacing:'0.04em', textTransform:'uppercase' }}>
        💸 My Recent Payments
      </div>
      <div style={{ fontSize:'0.74rem', color:'#9ca3af', marginBottom:10 }}>
        Tap a row to see exactly where the money went.
      </div>
      {visible.map(s => {
        const isExpanded = (expandedId === s.id);
        const data = breakdownById[s.id];
        const date = s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('en-IN',
                       { day:'numeric', month:'short', year:'numeric' }) : '';
        return (
          <div key={s.id} style={{ borderTop:'1px solid #f3f4f6', paddingTop:8, paddingBottom:8 }}>
            <button onClick={() => toggle(s.id)}
              style={{ width:'100%', textAlign:'left', background:'none', border:'none',
                       cursor:'pointer', padding:0 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:'0.95rem', color:'#1E3A6E' }}>
                      ₹{parseFloat(s.amount).toLocaleString('en-IN')}
                    </span>
                    <PaymentStatusPill status={s.status} />
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>
                    {date} · UTR <span style={{ fontFamily:'monospace' }}>{(s.utr || '').slice(0, 22)}{s.utr?.length > 22 ? '…' : ''}</span>
                  </div>
                </div>
                <div style={{ color:'#9ca3af', fontSize:'0.85rem', marginLeft:8, flexShrink:0 }}>
                  {isExpanded ? '▲' : '▼'}
                </div>
              </div>
            </button>

            {isEditable(s) && (editingId === s.id ? (
              <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:6 }}>
                <input
                  value={editUtr}
                  onChange={e => setEditUtr(e.target.value)}
                  placeholder="Transaction reference (UTR)"
                  autoFocus
                  style={{ fontFamily:'monospace', fontSize:'0.82rem', padding:'8px 10px',
                           border:'1px solid #d1d5db', borderRadius:8, textTransform:'uppercase' }}
                />
                {String(s.utr || '').includes('_SPLIT_') && (
                  <div style={{ fontSize:'0.68rem', color:'#6b7280' }}>
                    This updates the reference across all parts of this split payment.
                  </div>
                )}
                {editError && <div style={{ color:'#dc2626', fontSize:'0.72rem' }}>{editError}</div>}
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => saveUtr(s.id)} disabled={editSaving}
                    style={{ background:'#1E3A6E', color:'white', border:'none', borderRadius:8,
                             padding:'7px 14px', fontSize:'0.78rem', fontWeight:700,
                             cursor: editSaving ? 'default' : 'pointer', opacity: editSaving ? 0.6 : 1 }}>
                    {editSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={cancelEdit} disabled={editSaving}
                    style={{ background:'none', color:'#6b7280', border:'1px solid #d1d5db',
                             borderRadius:8, padding:'7px 14px', fontSize:'0.78rem', cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => startEdit(s)}
                style={{ marginTop:4, background:'none', border:'none', color:'#1E3A6E',
                         fontSize:'0.72rem', fontWeight:700, cursor:'pointer', padding:'2px 0',
                         textDecoration:'underline' }}>
                ✏️ Edit transaction reference
              </button>
            ))}

            {isExpanded && (
              <div style={{ marginTop:10, padding:'10px 12px', background:'#f9fafb',
                            borderRadius:8, fontSize:'0.8rem' }}>
                {!data && <div style={{ color:'#9ca3af' }}>Loading breakdown…</div>}
                {data?.error && <div style={{ color:'#dc2626' }}>Failed to load breakdown.</div>}
                {data && !data.error && data.forecast && (
                  <ForecastBreakdown data={data} />
                )}
                {data && !data.error && !data.forecast && (
                  <ConfirmedBreakdown data={data} />
                )}
              </div>
            )}
          </div>
        );
      })}
      {submissions.length > visible.length && (
        <div style={{ marginTop:10, fontSize:'0.72rem', color:'#9ca3af', textAlign:'center' }}>
          Showing {visible.length} of {submissions.length} payments.
        </div>
      )}
    </div>
  );
}

function PaymentStatusPill({ status }) {
  const cfg = {
    confirmed: { bg:'#dcfce7', fg:'#15803d', label:'CONFIRMED' },
    pending:   { bg:'#fef3c7', fg:'#92400e', label:'PENDING'   },
    matched:   { bg:'#dbeafe', fg:'#1d4ed8', label:'MATCHED'   },
    review:    { bg:'#fee2e2', fg:'#991b1b', label:'REVIEW'    },
    rejected:  { bg:'#fecaca', fg:'#991b1b', label:'REJECTED'  },
    unmatched: { bg:'#f3f4f6', fg:'#6b7280', label:'UNMATCHED' },
  };
  const c = cfg[status] || cfg.pending;
  return (
    <span style={{ background:c.bg, color:c.fg, padding:'2px 8px', borderRadius:6,
                   fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.04em' }}>
      {c.label}
    </span>
  );
}

function ForecastBreakdown({ data }) {
  const sub = data.submission;
  return (
    <div>
      <div style={{ color:'#6b7280', marginBottom:6 }}>
        {sub.status === 'review' ? (
          <>⚠️ This payment needs admin review{sub.review_reason ? ` — "${sub.review_reason}"` : ''}.</>
        ) : (
          <>⏳ Awaiting bank confirmation. When confirmed, your money will apply to:</>
        )}
      </div>
      {sub.target_label ? (
        <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6,
                      padding:'8px 10px', color:'#1d4ed8', fontWeight:600 }}>
          🎯 {sub.target_label}
        </div>
      ) : (
        <div style={{ color:'#374151' }}>
          Oldest unpaid {sub.allocation_type === 'fines' ? 'fines' : sub.allocation_type === 'mixed' ? 'fees and fines' : 'fees'} first
          {sub.allocation_note ? <span style={{ color:'#9ca3af' }}> · note: {sub.allocation_note}</span> : null}
        </div>
      )}
      {sub.beneficiary_name && sub.beneficiary_ucc_id && sub.submitter_ucc_id !== sub.beneficiary_ucc_id && (
        <div style={{ marginTop:6, fontSize:'0.74rem', color:'#6b7280' }}>
          For: <strong>{sub.beneficiary_name}</strong> ({sub.beneficiary_ucc_id})
        </div>
      )}
    </div>
  );
}

function ConfirmedBreakdown({ data }) {
  const sub = data.submission;
  const lines = data.allocations.filter(a => !a.reversed);
  const totalApplied = data.total_applied;
  const creditConsumed = data.credit_consumed;
  // Money that overflowed into advance credit on this submission
  const overflow = lines.filter(a => a.kind === 'credit' && a.notes === 'advance_credit')
                        .reduce((s, a) => s + a.amount, 0);

  return (
    <div>
      {creditConsumed > 0 && (
        <div style={{ marginBottom:8, padding:'7px 10px', background:'#fef3c7',
                      border:'1px solid #fde68a', borderRadius:6, color:'#92400e' }}>
          💰 ₹{Math.round(creditConsumed).toLocaleString('en-IN')} of advance credit was used first
        </div>
      )}
      {lines.length === 0 && (
        <div style={{ color:'#9ca3af' }}>No allocations recorded.</div>
      )}
      {lines.filter(a => a.kind !== 'credit' || a.notes !== 'advance_credit_consumed').map(a => (
        <div key={a.id} style={{ display:'flex', justifyContent:'space-between',
                                 padding:'5px 0', borderBottom:'1px dashed #e5e7eb' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'#374151', fontWeight:600 }}>
              {a.kind === 'fee'  && '💰 '}
              {a.kind === 'fine' && '⚠️ '}
              {a.kind === 'credit' && '🪙 '}
              {a.label}
            </div>
            {a.notes === 'targeted' && (
              <div style={{ fontSize:'0.7rem', color:'#1d4ed8' }}>You picked this specifically</div>
            )}
            {a.notes === 'auto_oldest_first' && (
              <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>Auto-applied (oldest first)</div>
            )}
            {a.notes === 'advance_credit' && (
              <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>Surplus parked for next payment</div>
            )}
          </div>
          <div style={{ fontWeight:700, color:'#1E3A6E', marginLeft:8 }}>
            ₹{Math.round(a.amount).toLocaleString('en-IN')}
          </div>
        </div>
      ))}
      <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #e5e7eb',
                    display:'flex', justifyContent:'space-between', fontSize:'0.78rem' }}>
        <span style={{ color:'#6b7280' }}>Total applied to dues</span>
        <strong style={{ color:'#15803d' }}>₹{Math.round(totalApplied).toLocaleString('en-IN')}</strong>
      </div>
      {overflow > 0 && (
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem' }}>
          <span style={{ color:'#6b7280' }}>Parked as advance credit</span>
          <strong style={{ color:'#92400e' }}>₹{Math.round(overflow).toLocaleString('en-IN')}</strong>
        </div>
      )}
      {sub.beneficiary_name && sub.beneficiary_ucc_id && sub.submitter_ucc_id !== sub.beneficiary_ucc_id && (
        <div style={{ marginTop:6, fontSize:'0.74rem', color:'#6b7280' }}>
          Paid for: <strong>{sub.beneficiary_name}</strong> ({sub.beneficiary_ucc_id})
        </div>
      )}
    </div>
  );
}

export function MyFeesPage() {
  const [fees, setFees] = useState([]);
  const [totalDue, setTotalDue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [attendanceByMonth, setAttendanceByMonth] = useState({});
  const [advanceCredit, setAdvanceCredit] = useState(0); // Phase 1 (c.2)

  useEffect(() => {
    Promise.all([
      api.get('/fees/my'),
      api.get('/attendance/my'),
      // Phase 1 (c.2): pull member's advance credit balance
      api.get('/payments/credits/my').catch(() => ({ data: { advance_credit: 0 } })),
    ]).then(([feesRes, attRes, creditRes]) => {
      setFees(feesRes.data.fees || []);
      setTotalDue(feesRes.data.totalDue || 0);
      setAdvanceCredit(parseFloat(creditRes.data.advance_credit || 0));
      // Group attendance records by year-month
      const byMonth = {};
      (attRes.data.attendance || []).forEach(a => {
        if (!a.match_date) return;
        const d = new Date(a.match_date);
        const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
        if (!byMonth[key]) byMonth[key] = [];
        byMonth[key].push(a);
      });
      setAttendanceByMonth(byMonth);
    }).finally(() => setLoading(false));
  }, []);

  const FEE_SLABS = { 4: 6000, 3: 7000, 2: 8000, 1: 9000, 0: 10000 };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>💰 My Fees</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Season 2026</p>
      </div>

      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        {/* Phase 1 (c.2): advance credit banner — shown only if non-zero */}
        {advanceCredit > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '1px solid #86efac', borderRadius: 12, padding: '14px 16px',
            marginBottom: 'var(--space-md)', display:'flex', alignItems:'center', gap:14
          }}>
            <div style={{ fontSize:'1.6rem' }}>💰</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.05rem', color:'#15803d' }}>
                ₹{advanceCredit.toLocaleString('en-IN')} advance credit
              </div>
              <div style={{ fontSize:'0.78rem', color:'#16a34a', marginTop:2 }}>
                On account · applies when your next payment is confirmed
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="stat-grid mb-md">
          <div className="stat-box">
            <div className="stat-box__value" style={{ color: totalDue > 0 ? 'var(--danger)' : 'var(--success)' }}>
              ₹{Math.round(totalDue).toLocaleString('en-IN')}
            </div>
            <div className="stat-box__label">Total Outstanding</div>
          </div>
          <div className="stat-box">
            <div className="stat-box__value">{fees.filter(f => f.status === 'paid').length}</div>
            <div className="stat-box__label">Months Cleared</div>
          </div>
        </div>

        {totalDue > 0 && (
          <Link to="/payment" className="btn btn-gold btn-block mb-md">
            💳 Record Payment Now
          </Link>
        )}

        {/* Fee slab guide */}
        <div className="card mb-md">
          <div className="card__header"><span className="card__title">Monthly Fee Structure</span></div>
          <div className="card__body">
            {Object.entries(FEE_SLABS).map(([sessions, amount]) => (
              <div key={sessions} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--ucc-gray-100)' }}>
                <span style={{ color: 'var(--ucc-gray-700)', fontSize: '0.9rem' }}>
                  {sessions === '4' ? '4+ sessions' : `${sessions} session${sessions !== '1' ? 's' : ''}`}
                </span>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: '#1E3A6E' }}>₹{amount.toLocaleString('en-IN')}</span>
              </div>
            ))}
            <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 8 }}>
              Due: 1st Sunday of following month • Receipt must be sent to UCC Official number
            </div>
          </div>
        </div>

        {/* Fee history */}
        {fees.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">💰</div>
            <div className="empty-state__title">No fee records yet</div>
          </div>
        ) : (
          <div className="card">
            {fees.map(fee => {
              const balance = parseFloat(fee.amount_due) - parseFloat(fee.amount_paid);
              return (
                <div key={fee.id} className="list-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem' }}>
                        {MONTH_NAMES[fee.month]} {fee.year}
                      </span>
                      <StatusBadge status={fee.status} />
                    </div>
                    {(() => {
                      const key = `${fee.year}-${fee.month}`;
                      const records = (attendanceByMonth[key] || []).sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
                      const STATUS_LABELS = {
                        present: 'Present', absent: 'Absent', rested: 'Rested',
                        series_rested: 'Series Rested', exempted: 'Exempted',
                        opt_out_fee_only: 'Opt Out', opt_out_full: 'Opt Out', holiday: 'Holiday',
                      };
                      const STATUS_COLORS = {
                        present: '#16a34a', absent: '#dc2626', rested: '#2563eb',
                        series_rested: '#7c3aed', exempted: '#d97706',
                        opt_out_fee_only: '#0891b2', opt_out_full: '#0891b2', holiday: '#6b7280',
                      };
                      if (records.length === 0) {
                        // Break month — no matches were held, so there was nothing
                        // to attend. Show a holiday label instead of "0 sessions".
                        if (Number(fee.matches_held) === 0) return (
                          <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏖️</span> Holiday — no matches this month
                          </div>
                        );
                        return (
                          <div style={{ fontSize: '0.78rem', color: 'var(--ucc-gray-500)' }}>
                            {fee.sessions_attended ?? 0} session{fee.sessions_attended !== 1 ? 's' : ''} attended
                          </div>
                        );
                      }
                      return (
                        <div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--ucc-gray-500)', marginBottom: 4 }}>
                            {fee.sessions_attended ?? 0} session{fee.sessions_attended !== 1 ? 's' : ''} attended
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                            {records.map((a, i) => {
                              const d = new Date(a.match_date);
                              const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                              const label = STATUS_LABELS[a.status] || a.status;
                              const color = STATUS_COLORS[a.status] || '#6b7280';
                              return (
                                <span key={i} style={{ fontSize: '0.7rem', color: '#374151', whiteSpace: 'nowrap' }}>
                                  {dateStr}: <span style={{ fontWeight: 700, color }}>{label}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Charges and rebates are shown as their own labelled
                        lines with the base fee above them, never folded into
                        the total — a member should be able to see exactly why
                        a month differs from the usual amount. */}
                    {Array.isArray(fee.adjustments) && fee.adjustments.length > 0 && (
                      <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed #e5e7eb' }}>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Monthly fee</span>
                          <span>₹{parseFloat(fee.base_amount ?? fee.amount_due).toLocaleString('en-IN')}</span>
                        </div>
                        {fee.adjustments.map((a, i) => (
                          <div key={i} style={{ fontSize: '0.72rem', display: 'flex', justifyContent: 'space-between',
                            color: a.kind === 'charge' ? '#b45309' : '#166534', fontWeight: 600 }}>
                            <span>{a.label}</span>
                            <span>{a.kind === 'charge' ? '+' : '−'}₹{parseFloat(a.amount).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {fee.late_fine_total > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 2 }}>
                        Late charges: ₹{parseFloat(fee.late_fine_total).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: balance > 0 ? 'var(--danger)' : 'var(--success)' }}>
                      ₹{parseFloat(fee.amount_due).toLocaleString('en-IN')}
                    </div>
                    {balance > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--danger)', fontWeight: 600 }}>₹{balance.toLocaleString('en-IN')} pending</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Phase 2: where did my money go */}
        <PaymentsBreakdownSection />
      </div>
    </div>
  );
}

export function MyFinesPage() {
  const [fines, setFines] = useState([]);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [loading, setLoading] = useState(true);
  const [advanceCredit, setAdvanceCredit] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/fines/my'),
      api.get('/payments/credits/my').catch(() => ({ data: { advance_credit: 0 } }))
    ]).then(([finesRes, creditRes]) => {
      setFines(finesRes.data.fines || []);
      setTotalUnpaid(finesRes.data.totalUnpaid || 0);
      setAdvanceCredit(parseFloat(creditRes.data?.advance_credit || 0));
    }).finally(() => setLoading(false));
  }, []);

  const CATEGORY_LABELS = {
    1: { label: 'Cat. 1', color: 'var(--warning)' },
    2: { label: 'Cat. 2', color: 'var(--danger)' },
    3: { label: 'Cat. 3', color: '#7b1fa2' },
    4: { label: 'Late', color: 'var(--info)' },
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>⚠️ My Fines</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Pay by the deposit deadline or escalation kicks in</p>
      </div>

      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        <div className="stat-grid mb-md">
          <div className="stat-box">
            <div className="stat-box__value" style={{ color: totalUnpaid > 0 ? 'var(--danger)' : 'var(--success)' }}>
              ₹{Math.round(totalUnpaid).toLocaleString('en-IN')}
            </div>
            <div className="stat-box__label">Total Unpaid</div>
          </div>
          <div className="stat-box">
            <div className="stat-box__value">{fines.filter(f => ['paid','waived'].includes(f.status)).length}</div>
            <div className="stat-box__label">Cleared</div>
          </div>
        </div>

        {/* Phase 1 (c.2): advance credit banner */}
        {advanceCredit > 0 && (
          <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10,
            padding:'12px 14px', marginBottom:'var(--space-md)',
            display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontSize:'1.4rem' }}>💰</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, color:'#92400e', fontSize:'0.95rem' }}>
                ₹{advanceCredit.toLocaleString('en-IN')} advance credit on your account
              </div>
              <div style={{ fontSize:'0.78rem', color:'#92400e', opacity:0.85, marginTop:2 }}>
                This will apply when your next payment is confirmed.
              </div>
            </div>
          </div>
        )}

        {totalUnpaid > 0 && (
          <Link to="/payment" className="btn btn-gold btn-block mb-md">
            💳 Pay Fines Now
          </Link>
        )}

        {fines.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">✅</div>
            <div className="empty-state__title">No fines!</div>
            <div className="empty-state__text">Keep it up — stay disciplined.</div>
          </div>
        ) : (
          <div className="card">
            {fines.map(fine => {
              const catInfo = CATEGORY_LABELS[fine.category] || CATEGORY_LABELS[1];
              const isPaid = ['paid', 'waived'].includes(fine.status);
              const balance = parseFloat(fine.amount) - parseFloat(fine.amount_paid || 0);
              return (
                <div key={fine.id} className="list-item">
                  <div style={{
                    width: 8, height: 40, borderRadius: 4, background: catInfo.color, flexShrink: 0
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', color: catInfo.color }}>
                        {catInfo.label}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--ucc-gray-800)' }}>{fine.reason}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>
                      {new Date(fine.issued_at).toLocaleDateString('en-IN')} • Issued by {fine.issued_by_name || 'Admin'}
                    </div>
                    {fine.compounding_weeks > 0 && !isPaid && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--danger)', marginTop: 2 }}>
                        +₹{(fine.compounding_weeks * 1000).toLocaleString('en-IN')} compounding ({fine.compounding_weeks} weeks)
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem', color: isPaid ? 'var(--success)' : 'var(--danger)' }}>
                      ₹{parseFloat(fine.amount).toLocaleString('en-IN')}
                    </div>
                    <span className={`badge ${isPaid ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '0.65rem' }}>
                      {fine.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Phase 2: where did my money go */}
        <PaymentsBreakdownSection />
      </div>
    </div>
  );
}

export default MyFeesPage;
